import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flag, Loader2, Mail, Phone, ChevronRight, MessageCircle, RefreshCw, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

/* /admin/customers/priority — Priority Customers.
   Anyone flagged Priority appears here automatically. Priority is a workflow
   over the same order records, not a separate order database. */

const AUBERGINE = "#3E1F46";
const PLUM = "#5B2A4F";

export const STATUS_STYLE = {
  "Invited":              { bg: "#F7E8C8", fg: "#7A5107" },
  "Customer Reviewing":   { bg: "#E7DFF2", fg: "#4B2E63" },
  "Changes Requested":    { bg: "#F9DCC4", fg: "#8A4A12" },
  "Admin Review":         { bg: "#FBD5D5", fg: "#8E2323" },
  "Waiting for Customer": { bg: "#FBF0DC", fg: "#8A5D12" },
  "Confirmed":            { bg: "#1F5B3F", fg: "#FFFFFF" },
  "Ready for Packing":    { bg: "#3E1F46", fg: "#FFFFFF" },
  "Resolved":             { bg: "#3C2A1E", fg: "#FFFFFF" },
};

export const StatusPill = ({ status, testid }) => {
  const s = STATUS_STYLE[status] || { bg: "#F3EBDD", fg: "#6E1E3A" };
  return (
    <span data-testid={testid} className="inline-flex px-2 py-0.5 rounded-full text-[9px] tracking-[0.16em] uppercase font-bold whitespace-nowrap"
      style={{ backgroundColor: s.bg, color: s.fg }}>{status || "Invited"}</span>
  );
};

const PAY_LABEL = { paid: "Paid", unpaid: "Unpaid", partially_paid: "Partially paid", none: "No orders" };
const when = (iso) => iso ? new Date(iso).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function AdminPrioritisedCustomers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/priority/customers");
      setRows(r.data?.customers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the priority list.");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const copyAllEmails = async () => {
    const list = rows.map((c) => c.email).filter(Boolean).join(", ");
    if (!list) return toast.error("None of these customers have an email address on file.");
    try {
      await navigator.clipboard.writeText(list);
      toast.success(`${rows.filter((c) => c.email).length} addresses copied — paste into Gmail's BCC field.`);
    } catch { toast.error("Your browser blocked the clipboard — open Mass Email instead."); }
  };

  return (
    <div data-testid="admin-prioritised" className="space-y-6 max-w-6xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Customers</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-1.5" style={{ color: AUBERGINE }}>Priority Customers</h1>
          <p className="font-serif italic mt-1.5 text-[#6E5849] max-w-3xl text-sm leading-relaxed">
            Anyone marked Priority lands here automatically. Open a customer to review their order together, message them
            and move it into packing once you're both happy.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={copyAllEmails} disabled={rows.length === 0} data-testid="priority-copy-emails"
            title="Copy every priority customer's email address for Gmail"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#3E1F46] text-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
            <Copy className="w-4 h-4" /> Copy all emails
          </button>
          <Link to="/admin/mass-email" data-testid="priority-mass-email-link"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-[#E8DFCB] bg-white text-[#3C2A1E] text-[11px] font-bold uppercase tracking-[0.14em]">
            <Mail className="w-4 h-4" /> Mass email
          </Link>
          <button type="button" onClick={reload} data-testid="priority-refresh"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-[#E8DFCB] bg-white text-[#3C2A1E] text-[11px] font-bold uppercase tracking-[0.14em]">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      {loading ? (
        <div className="p-8 text-center text-sm text-[#7A6A55]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border rounded p-8 text-center text-sm italic text-[#7A6A55]" style={{ borderColor: "#E0D1C8" }} data-testid="priority-empty">
          No customers in the Priority Queue yet. Open any customer and hit the <Flag className="w-3.5 h-3.5 inline mb-0.5" /> flag in their profile drawer.
        </div>
      ) : (
        <div className="bg-white border rounded-md overflow-x-auto" style={{ borderColor: "#E0D1C8" }}>
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-[9px] tracking-[0.2em] uppercase text-[#9C8B73] bg-[#FBF6E9] [&>th]:whitespace-nowrap">
                <th className="text-left px-4 py-2.5">Customer</th>
                <th className="px-3 py-2.5">Orders</th>
                <th className="px-3 py-2.5">Total value</th>
                <th className="px-3 py-2.5">Payment</th>
                <th className="px-3 py-2.5">Priority status</th>
                <th className="px-3 py-2.5">Confirmation</th>
                <th className="px-3 py-2.5">Last contact</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0E8D8]">
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-[#FBF6E9] transition-colors" data-testid={`priority-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/admin/customers/priority/${c.id}`} className="font-serif text-[15px] hover:underline" style={{ color: AUBERGINE }}>
                      {c.name}
                    </Link>
                    <div className="text-xs text-[#7A6A55] flex gap-3 flex-nowrap overflow-hidden mt-0.5 whitespace-nowrap">
                      {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                      {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                      {!c.in_hq && <span className="text-[#A33030] font-semibold">No HQ account yet</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center font-bold">{c.order_count}</td>
                  <td className="px-3 py-3 text-center">NZ${Number(c.total_value).toFixed(2)}
                    {c.owing_value > 0 && <div className="text-[10px] text-[#A33030]">owing ${Number(c.owing_value).toFixed(2)}</div>}
                  </td>
                  <td className="px-3 py-3 text-center text-xs">{PAY_LABEL[c.payment_status] || c.payment_status}</td>
                  <td className="px-3 py-3 text-center"><StatusPill status={c.priority_status} testid={`priority-status-${c.id}`} /></td>
                  <td className="px-3 py-3 text-center text-xs">{c.confirmed_at ? `Confirmed ${when(c.confirmed_at)}` : "Awaiting customer"}</td>
                  <td className="px-3 py-3 text-center text-xs">{when(c.last_contact)}</td>
                  <td className="px-3 py-3 text-right">
                    {c.unread_from_customer > 0 && (
                      <span className="inline-flex items-center gap-1 mr-2 px-2 py-0.5 rounded-full bg-[#A33030] text-white text-[10px] font-bold" data-testid={`priority-unread-${c.id}`}>
                        <MessageCircle className="w-3 h-3" /> {c.unread_from_customer}
                      </span>
                    )}
                    <Link to={`/admin/customers/priority/${c.id}`} data-testid={`priority-open-${c.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: PLUM }}>
                      Open <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
